import React, { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { IconChecklist } from '@tabler/icons-react';
import { useTranslation } from "react-i18next";
import { DataTable } from "@/components/table";
import { Case, caseListSchema } from "@/views/cases/data/schema";
import { get_cases, CaseListingParams } from "@/api/case";
import { userCaseColumns } from "@/views/users/page/user_case_columns";
import DataTableSkeleton from "@/components/ui/data-table-skeleton";
import { Button } from "@/components/ui/button";
import { ChevronDown, ChevronUp } from "lucide-react";
import { useNavigate } from "@tanstack/react-router";

interface UserAssignedCasesTableProps {
  userId: string;
  initialMaximized?: boolean;
  setTotalCasesCount?: React.Dispatch<React.SetStateAction<number>>;
  setLoadingCases: React.Dispatch<React.SetStateAction<boolean>>;
  loadingCases: boolean;
  assignedCases: Case[];
  setAssignedCases: React.Dispatch<React.SetStateAction<Case[]>>;
}

export function UserAssignedCasesTable({
    userId,
    initialMaximized = false,
    setTotalCasesCount,
    setLoadingCases,
    loadingCases,
    assignedCases,
    setAssignedCases
}: UserAssignedCasesTableProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isMaximized, setIsMaximized] = useState(initialMaximized);
  const [casesPagesCnt, setCasesPagesCnt] = useState<number>(0);
  const [casesParams, setCasesParams] = useState<CaseListingParams>({
    page: 0,
    page_size: 5,
    ordering: null,
    name: null,
    types: null,
    assigned_to: userId,
  });

  const columns = userCaseColumns();

  useEffect(() => {
    if (userId && isMaximized) {
      setLoadingCases(true);
      get_cases({ ...casesParams, assigned_to: userId })
        .then((resp) => {
          const cases_list = caseListSchema.parse(resp.results);
          setAssignedCases(cases_list);
          if (setTotalCasesCount) {
            setTotalCasesCount(resp.count);
          }
          setCasesPagesCnt(Math.ceil(resp.count / casesParams.page_size));
        })
        .catch(() => {
          setAssignedCases([]);
          setCasesPagesCnt(0);
        })
        .finally(() => {
          setLoadingCases(false);
        });
    }
  }, [
    userId, casesParams, isMaximized,
    setLoadingCases, setTotalCasesCount, setAssignedCases
  ]);

  const handleCaseRowClick = (row: Case) => {
    navigate({
      to: `/cases/${row.id}`,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      state: { caseData: row, timestamp: Date.now() } as any,
    });
  };

  const toggleMaximized = () => setIsMaximized(!isMaximized);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <IconChecklist className="h-6 w-6" />
            <CardTitle>{t('assignedCases')}</CardTitle>
          </div>
          {!initialMaximized && (<Button variant="ghost" size="sm" onClick={toggleMaximized}>
            {isMaximized ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            <span className="sr-only">{isMaximized ? t('minimize') : t('maximize')}</span>
          </Button>)}
        </div>
        <CardDescription>{t('assignedCasesDescription')}</CardDescription>
      </CardHeader>
      {isMaximized && (
        <CardContent>
          {loadingCases ? (
            <DataTableSkeleton with_filter={false} columns={1} rows={casesParams.page_size} />
          ) : assignedCases.length > 0 ? (
            <DataTable<Case>
              columns={columns}
              data={assignedCases}
              params={casesParams}
              setParams={setCasesParams}
              pages={casesPagesCnt}
              onRowClick={handleCaseRowClick}
              hideHeader={true}
            />
          ) : (
            <p>{t('noCasesAssigned')}</p>
          )}
        </CardContent>
      )}
    </Card>
  );
}
